import { Group, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { useState } from "react";
import MetadataDocuments from "../MetadataDocuments";
import { ExpandGraph } from "./ExpandGraph";
import { resolveDefaultCategory } from "./services/defaultCategory";
import {
    type BucketSelection,
    DocumentViewMode,
    type DocumentViewsProps,
    type OverlayState,
} from "./types";

const VIEW_OPTIONS = [
    { value: DocumentViewMode.TABLE, label: "Table" },
    { value: DocumentViewMode.EXPLODE, label: "Overview" },
];

export const DocumentViews = ({
    index,
    rootLabel,
    datasourceLabel,
    fields,
    onFilteredCountChange,
}: DocumentViewsProps) => {
    const [mode, setMode] = useState<DocumentViewMode>(DocumentViewMode.TABLE);
    const [initialFilter, setInitialFilter] = useState<BucketSelection[]>();
    const [tableKey, setTableKey] = useState(0);
    const [category, setCategory] = useState(() =>
        fields.length > 0 ? resolveDefaultCategory(datasourceLabel, fields) : "",
    );
    const [selected, setSelected] = useState<BucketSelection | undefined>(undefined);
    const [overlay, setOverlay] = useState<OverlayState | undefined>(undefined);
    const [gridPage, setGridPage] = useState(0);

    const openFiltered = (selection: BucketSelection[]) => {
        setOverlay(undefined);
        setInitialFilter(selection);
        setTableKey((current) => current + 1);
        setMode(DocumentViewMode.TABLE);
    };

    const selectValue = (selection: BucketSelection) => {
        setOverlay(undefined);
        setSelected((current) =>
            current?.field === selection.field && current?.value === selection.value
                ? undefined
                : selection,
        );
    };

    const clearSelection = () => {
        setOverlay(undefined);
        setSelected(undefined);
    };

    const isOverview = mode === DocumentViewMode.EXPLODE;
    const categoryLabel = fields.find((field) => field.value === category)?.label ?? category;

    return (
        <Stack gap="md">
            <Group gap="lg" align="center">
                <SegmentedControl
                    data={VIEW_OPTIONS}
                    value={mode}
                    onChange={(value) => {
                        const nextMode = value as DocumentViewMode;
                        setMode(nextMode);
                        if (nextMode === DocumentViewMode.EXPLODE) {
                            setInitialFilter(undefined);
                            onFilteredCountChange?.(undefined);
                        }
                    }}
                    w="fit-content"
                />
                {isOverview && (
                    <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={500}>
                            Category
                        </Text>
                        <Select
                            placeholder="Select a field"
                            data={fields}
                            value={category}
                            onChange={(value) => {
                                setCategory(value ?? "");
                                setSelected(undefined);
                                setOverlay(undefined);
                                setGridPage(0);
                            }}
                            w={220}
                            searchable
                        />
                    </Group>
                )}
            </Group>

            {isOverview ? (
                <ExpandGraph
                    index={index}
                    rootLabel={rootLabel}
                    category={category}
                    categoryLabel={categoryLabel}
                    fields={fields}
                    selected={selected}
                    overlay={overlay}
                    gridPage={gridPage}
                    onGridPage={setGridPage}
                    onSelect={selectValue}
                    onClearSelection={clearSelection}
                    onOpen={openFiltered}
                    onOpenOverlay={setOverlay}
                    onCloseOverlay={() => setOverlay(undefined)}
                />
            ) : (
                <MetadataDocuments
                    key={`${index}-${tableKey}`}
                    index={index}
                    initialFilter={initialFilter}
                    onFilteredCountChange={onFilteredCountChange}
                />
            )}
        </Stack>
    );
};
